/* eslint-disable eqeqeq */
import { useEffect, useRef, useState } from 'react';
import {
  formatDecimalDisplay,
  formatIntegerDisplay,
  useDebouncedCallback,
} from './formUtils';

/**
 * Phase 6e2a — the §52.1 shared DOM grammar as a React kit (THE conversion
 * spec for `<FormSubtab>`/`<FieldRow>`): every legacy form tmpl follows it.
 *
 *  - Subtab li: `input[type=checkbox][data-subtab=<name>]#<type>_<cid>_<name>`
 *    (components add name="ahu_left_bar" class="sublist-ctrl-checkbox") +
 *    label + `ul.sublist` body. Independent checkboxes — multiple open
 *    (parity 308); state round-trips via the chassis subtabs dict.
 *  - Field row: `li > div.single-line` with `span.selection` mirror (the
 *    visible text — the select is visually hidden by CSS, parity 346),
 *    select with `option value="None"` placeholder "- -", `span.label`,
 *    optional `span.unit`.
 *  - Inputs: `.model_field`/`.section_field`/`.<comp>_params_field` +
 *    data-formatter_type decimal|integer + data-min/max/step; readonly →
 *    tabindex −1. 800 ms debounced input + change commit (§49.3 binding).
 *  - ifCompare loose-`==` semantics preserved: option selection compares via
 *    `looseEq` (string ids vs number ids compare equal — parity 345).
 *  - Tab keyCode 9 manual nav: advance index, skip tabindex −1,
 *    preventDefault (§49.3 / §44.2 gap / parity 309) — `handleTabToNext`.
 */

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------
export function looseEq(a: unknown, b: unknown): boolean {
  if (a === null || a === undefined || b === null || b === undefined)
    return a == b;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (a as any) == (b as any) || String(a) === String(b);
}

/**
 * `helpers.js:159` `truncate_decimal` — truncate (never round) to 2 dp and
 * ALWAYS emit both places, `''` for empty/non-numeric. Every legacy tmpl that
 * prints a dimension goes through it, so `6` reads `6.00` and `6.567` reads
 * `6.56`. Keep the `.toFixed(2)`: dropping it is what made the damper and
 * louver AW/OD rows print bare integers.
 */
export function truncDec(v: unknown): string {
  if (v === '' || v == null || Number.isNaN(Number(v))) return '';
  return (Math.trunc(Number(v) * 100) / 100).toFixed(2);
}

export interface OptionRow {
  id: number | string;
  [key: string]: unknown;
}

/** CMS option label resolution (tmpl renders name/value/series/label). */
export function optionLabel(row: OptionRow, labelKey?: string): string {
  if (labelKey != null) return String(row[labelKey] ?? '');
  return String(row.name ?? row.value ?? row.series ?? row.label ?? row.model ?? '');
}

/** Dotted-path option-list reader over the componentCms doc (§49.3 step 5). */
export function cmsList(
  doc: Record<string, unknown> | undefined,
  path: string
): OptionRow[] {
  if (!doc) return [];
  let node: unknown = doc;
  for (const seg of path.split('.')) {
    if (node == null || typeof node !== 'object') return [];
    node = (node as Record<string, unknown>)[seg];
  }
  return Array.isArray(node) ? (node as OptionRow[]) : [];
}

/** §49.3 Tab keyCode 9 manual navigation (bind on the <form> keydown). */
export function handleTabToNext(e: React.KeyboardEvent<HTMLFormElement>): void {
  if (e.key !== 'Tab') return;
  const form = e.currentTarget;
  const focusables = Array.from(
    form.querySelectorAll<HTMLElement>('input, select')
  ).filter((el) => el.tabIndex !== -1 && !el.hidden && el.offsetParent !== null);
  const active = document.activeElement as HTMLElement | null;
  const idx = active ? focusables.indexOf(active) : -1;
  const next = focusables[(idx + 1) % Math.max(focusables.length, 1)];
  if (next) {
    e.preventDefault();
    next.focus();
  }
}

// ---------------------------------------------------------------------------
// <FormSubtab>
// ---------------------------------------------------------------------------
interface FormSubtabProps {
  /** legacy tmpl id prefix: `<type>_<cid>_<subtab>` */
  type: string;
  cid: number | string;
  name: string;
  label: string;
  open: boolean;
  onToggle: (name: string, open: boolean) => void;
  /** extra classes on the sublist ul (e.g. non-clickable, performance-sublist) */
  sublistClass?: string;
  /** Dev-sync J6 — `data-season` on the sublist ul (eliminator/silencer
   * form.tmpl `<ul class="sublist" data-season="{{section.season_value}}">`).
   * Kept for the merged [data-season] CSS gates even though JSX also gates
   * the rows (survey §C5 item 4). */
  sublistDataSeason?: string;
  /** legacy first-subtab labels carry class "hide" (per-tmpl census). */
  labelHidden?: boolean;
  /** extra classes on the wrapping li (e.g. group-with-include-checkbox). */
  liClass?: string;
  liId?: string;
  /** form-heading chrome rendered BEFORE the checkbox (frame groups). */
  headerChrome?: React.ReactNode;
  children: React.ReactNode;
}

export const FormSubtab = ({
  type,
  cid,
  name,
  label,
  open,
  onToggle,
  sublistClass,
  sublistDataSeason,
  labelHidden,
  liClass,
  liId,
  headerChrome,
  children,
}: FormSubtabProps) => {
  const id = `${type}_${cid}_${name}`;
  return (
    <li className={liClass} id={liId}>
      {headerChrome != null && <div className="form-heading">{headerChrome}</div>}
      <input
        id={id}
        data-subtab={name}
        type="checkbox"
        name="ahu_left_bar"
        className="sublist-ctrl-checkbox"
        checked={open}
        onChange={(e) => onToggle(name, e.target.checked)}
      />
      <label htmlFor={id} className={labelHidden ? 'hide' : undefined}>
        {label}
        <i className="icon-add" />
      </label>
      <ul
        className={`sublist${sublistClass ? ` ${sublistClass}` : ''}`}
        data-season={sublistDataSeason}
      >
        {open ? children : null}
      </ul>
    </li>
  );
};

// ---------------------------------------------------------------------------
// <SelectRow> — span.selection mirror + hidden-by-CSS select (parity 346)
// ---------------------------------------------------------------------------
interface SelectRowProps {
  name: string;
  /** model_field | section_field | `<comp>_params_field` (+ size classes). */
  fieldClass: string;
  value: unknown;
  options: OptionRow[];
  labelKey?: string;
  /** option value source (default 'id' — eliminator mtl uses the mtl string). */
  valueKey?: string;
  label: React.ReactNode;
  unit?: string;
  required?: boolean;
  disabled?: boolean;
  hideNone?: boolean;
  /** 6e2b verifier — explicit legacy None-option mode: 'visible' renders
   * `<option value="None">- -</option>` (no hide class), 'hidden' renders it
   * with class="hide", 'omit' renders no None option. Wins over hideNone.
   * (Legacy accessory tmpls use all three per select — §52.1.) */
  noneMode?: 'visible' | 'hidden' | 'omit';
  /** row div class override (legacy long_unit / single-line / bespoke). */
  rowClass?: string;
  /** selection span extra class (medium-selection etc.). */
  selectionClass?: string;
  /** selection span inline style — a few legacy tmpls nudge the mirror with
   * `style="margin-left: …"` instead of a class (fan form.tmpl:161). */
  selectionStyle?: React.CSSProperties;
  /** select inline style — same story on the control itself: the filter's
   * Supplied By select carries `style="min-width: 200px"` (filter
   * form.tmpl:165), which overrides its `width: calc(32.5% - 4px)` class. */
  selectStyle?: React.CSSProperties;
  onChange: (raw: string) => void;
  /** extra row content (paired selects render two in one single-line). */
  children?: React.ReactNode;
}

/** Bare select + selection mirror — for legacy multi-control rows (no li). */
export const BareSelect = ({
  name,
  fieldClass,
  value,
  options,
  labelKey,
  valueKey,
  disabled,
  hideNone,
  noneMode,
  selectionClass,
  selectionStyle,
  selectStyle,
  onChange,
}: Omit<SelectRowProps, 'label' | 'unit' | 'required' | 'rowClass' | 'children'>) => {
  const ovalue = (o: OptionRow) => String(valueKey ? o[valueKey] : o.id);
  const current = options.find((o) => looseEq(valueKey ? o[valueKey] : o.id, value));
  // noneMode (when given) wins over the older hideNone boolean.
  const mode = noneMode ?? (hideNone ? 'omit' : 'hidden');
  // DOM fallback. Handlebars only ever stamps `selected` on an option it
  // MATCHED (`{{#ifCompare id '==' ...}}`), so a stored value that is absent
  // from the CMS list leaves nothing selected and the browser falls back to
  // option 0 — the `- -` placeholder. React sets `value` on the select
  // instead, and an unmatched value gives selectedIndex −1, which renders the
  // control EMPTY. That is the whole difference between dev showing `- -` and
  // dev3 showing a blank line for a value the list no longer carries (the
  // value-keyed params rows — gas heater Configuration / Number of Tubes —
  // are the exposed ones: they are matched by string, not by a re-cascaded
  // id). Only applies where a None option exists to fall back to.
  const selected =
    value == null || value === '' || (!current && mode !== 'omit')
      ? 'None'
      : String(value);
  return (
    <>
      <span
        className={`selection${selectionClass ? ` ${selectionClass}` : ''}`}
        style={selectionStyle}
      >
        {current ? <span>{optionLabel(current, labelKey)}</span> : null}
      </span>
      {/* §JSX-WS — the legacy tmpls put a newline between every control; the
          controls are inline-block, so that collapsed space is 4px of real
          layout. Dropping it shifted every unit/multi-control row left. */}
      {' '}
      <select
        name={name}
        className={`${fieldClass}${disabled ? ' non-clickable' : ''}`}
        style={selectStyle}
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        {mode !== 'omit' && (
          <option value="None" className={mode === 'hidden' ? 'hide' : undefined}>
            - -
          </option>
        )}
        {options.map((o) => (
          <option key={ovalue(o)} value={ovalue(o)}>
            {optionLabel(o, labelKey)}
          </option>
        ))}
      </select>
    </>
  );
};

export const SelectRow = ({
  name,
  fieldClass,
  value,
  options,
  labelKey,
  valueKey,
  label,
  unit,
  required,
  disabled,
  hideNone,
  noneMode,
  rowClass,
  selectionClass,
  onChange,
  children,
}: SelectRowProps) => (
  <li>
    <div className={rowClass ?? (unit ? 'long_unit' : 'single-line')}>
      {unit && (
        <>
          <span className="unit">{unit}</span>{' '}
        </>
      )}
      <BareSelect
        name={name}
        fieldClass={fieldClass}
        value={value}
        options={options}
        labelKey={labelKey}
        valueKey={valueKey}
        disabled={disabled}
        hideNone={hideNone}
        noneMode={noneMode}
        selectionClass={selectionClass}
        onChange={onChange}
      />{' '}
      {children}
      <span className={`label${required ? ' required' : ''}`}>{label}</span>
    </div>
  </li>
);

// ---------------------------------------------------------------------------
// <InputRow> — text/number input with §49.3 binding semantics
// ---------------------------------------------------------------------------
interface InputRowProps {
  name: string;
  fieldClass: string;
  value: unknown;
  label: React.ReactNode;
  unit?: string;
  /** data-formatter_type (bindFormatter): decimal → toFixed(2)+commas;
   * integer → commas (fn.uni_tools number format). */
  formatter?: 'decimal' | 'integer';
  min?: number;
  max?: number;
  step?: number;
  required?: boolean;
  readOnly?: boolean;
  /** extra class for cross-form writes (e.g. `fan_<id>` ESP target §49.3). */
  extraClass?: string;
  /** row div class override; legacy unit rows use a CLASSLESS div, no-unit
   * rows use single-line (eliminator/filter tmpl grammar). */
  rowClass?: string;
  /** Dev-sync J6 — the LADB/LAWB placeholder-shows-entering pattern (survey
   * §J8a): legacy stamps `data-formatter_placeholder="true"` + the raw
   * entering value, then formatPlaceholder (tab.section.js:706-718) formats
   * it decimal-with-commas. Here the formatted placeholder renders directly. */
  placeholder?: unknown;
  /** input inline style — the filter's Sets input carries
   * `style="margin-left:5px"` (filter form.tmpl:171); no class supplies it. */
  style?: React.CSSProperties;
  /** '' → null normalization happens here (§49.3 field binding). */
  onCommit: (value: string | null) => void;
  children?: React.ReactNode;
}

/** Bare committing input (no li/div wrapper) — legacy multi-control rows. */
export const BareInput = ({
  name,
  fieldClass,
  value,
  formatter,
  min,
  max,
  step,
  readOnly,
  extraClass,
  placeholder,
  style,
  onCommit,
}: Omit<InputRowProps, 'label' | 'unit' | 'required' | 'rowClass' | 'children'>) => {
  const display =
    formatter === 'decimal'
      ? formatDecimalDisplay(value)
      : formatter === 'integer'
        ? formatIntegerDisplay(value)
        : String(value ?? '');
  const [text, setText] = useState(display);
  const focused = useRef(false);

  // Re-sync from the model when not typing (is_typing render guard §49.3).
  useEffect(() => {
    if (!focused.current) setText(display);
  }, [display]);

  const commit = (raw: string) => {
    const cleaned = raw.replace(/,/g, '').trim();
    onCommit(cleaned === '' ? null : cleaned);
  };
  // 800 ms debounced keyup (fn.uni_tools.addDebouncedChangeListener §49.3).
  const debouncedCommit = useDebouncedCallback(commit, 800);

  // formatPlaceholder (tab.section.js:706-718): decimal → toFixed(2)+commas,
  // integer → rounded+commas; a non-numeric/absent placeholder renders none.
  const placeholderText =
    placeholder == null || placeholder === '' || Number.isNaN(Number(placeholder))
      ? undefined
      : formatter === 'integer'
        ? formatIntegerDisplay(placeholder)
        : formatDecimalDisplay(placeholder);
  return (
    <input
      type="text"
      name={name}
      autoComplete="off"
      className={`${fieldClass}${extraClass ? ` ${extraClass}` : ''}${
        readOnly ? ' non-clickable' : ''
      }`}
      data-formatter_type={formatter}
      data-formatter_placeholder={placeholderText != null ? 'true' : undefined}
      data-min={min}
      data-max={max}
      data-step={step}
      placeholder={placeholderText}
      style={style}
      tabIndex={readOnly ? -1 : undefined}
      readOnly={readOnly}
      value={text}
      onFocus={() => {
        focused.current = true;
      }}
      onBlur={() => {
        focused.current = false;
        setText(display);
      }}
      onChange={(e) => {
        setText(e.target.value);
        debouncedCommit(e.target.value);
      }}
    />
  );
};

export const InputRow = ({
  name,
  fieldClass,
  value,
  label,
  unit,
  formatter,
  min,
  max,
  step,
  required,
  readOnly,
  extraClass,
  rowClass,
  onCommit,
  children,
}: InputRowProps) => (
  <li>
    <div className={rowClass ?? (unit ? undefined : 'single-line')}>
      {unit && (
        <>
          <span className="unit">{unit}</span>{' '}
        </>
      )}
      <BareInput
        name={name}
        fieldClass={fieldClass}
        value={value}
        formatter={formatter}
        min={min}
        max={max}
        step={step}
        readOnly={readOnly}
        extraClass={extraClass}
        onCommit={onCommit}
      />{' '}
      {children}
      <span className={`label${required ? ' required' : ''}`}>{label}</span>
    </div>
  </li>
);

// ---------------------------------------------------------------------------
// <CheckboxRow>
// ---------------------------------------------------------------------------
interface CheckboxRowProps {
  name: string;
  fieldClass: string;
  checked: boolean;
  label: React.ReactNode;
  disabled?: boolean;
  onCommit: (checked: boolean) => void;
}

export const CheckboxRow = ({
  name,
  fieldClass,
  checked,
  label,
  disabled,
  onCommit,
}: CheckboxRowProps) => (
  <li>
    <div className="single-line">
      <input
        type="checkbox"
        name={name}
        className={fieldClass}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCommit(e.target.checked)}
      />{' '}
      <span className="label">{label}</span>
    </div>
  </li>
);

// ---------------------------------------------------------------------------
// <CatalogueChrome> — count badge + btn-sync + reset span (§52.1 / parity 347)
// ---------------------------------------------------------------------------
interface CatalogueChromeProps {
  componentName: string;
  componentsName: string;
  count: number | undefined;
  fullCount: number | undefined;
  /** legacy per-type button class: vis-eliminator / vis-fan / vis-media... */
  visClass: string;
  /** extra button classes verbatim from the tmpl ('rating hide', 'hide',
   * 'mr-30'...). */
  buttonExtraClass?: string;
  /** wrapper div classes (legacy: 'ahu-header-btns right', sometimes +hide). */
  wrapperClass?: string;
  /** count span suppressed (filter frame: only when has_frame). */
  countVisible?: boolean;
  /** Smartlist <i> rendered (commented out for fan/motor in legacy). */
  showSmartlist?: boolean;
  showReset?: boolean;
  searchResultClass?: string;
  onToggleList: () => void;
  onReset: () => void;
}

/**
 * Legacy chrome grammar (per-tmpl): button classes
 * `btn btn-sync <vis-class> {{#unless count}}warning{{/unless}}
 *  {{#if count in (0,1)}} synced allow_reset{{/if}} clickable`
 * — classes live on the BUTTON, the wrapper stays plain.
 */
export const CatalogueChrome = ({
  componentName,
  componentsName,
  count,
  fullCount,
  visClass,
  buttonExtraClass,
  wrapperClass = 'ahu-header-btns right',
  countVisible = true,
  showSmartlist = true,
  showReset = true,
  searchResultClass,
  onToggleList,
  onReset,
}: CatalogueChromeProps) => {
  const synced = count == 1 || count == 0;
  const warning = !count;
  return (
    <div className={wrapperClass}>
      <button
        type="button"
        name={componentName}
        data-name={componentName}
        className={`btn btn-sync ${visClass}${
          buttonExtraClass ? ` ${buttonExtraClass}` : ''
        }${warning ? ' warning' : ''}${synced ? ' synced allow_reset' : ''} clickable`}
        onClick={onToggleList}
      >
        {countVisible && (
          <span
            className={`${componentsName}_search_result${
              searchResultClass ? ` ${searchResultClass}` : ''
            }`}
          >
            {`${count === undefined ? '' : count} / ${
              fullCount === undefined ? '' : fullCount
            }`}
          </span>
        )}
        {showSmartlist && <i className="icon-list btn-icon-sync">Smartlist</i>}
      </button>
      {showReset && (
        <span
          tooltip="Reset"
          className={`icon-refresh-1 ka-has-tooltip ${componentsName}_reset reset-btn`}
          onClick={onReset}
        >
          Reset
        </span>
      )}
    </div>
  );
};
